import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import { ColorsV2 } from 'app/constants/color';
import { calculateMinDate, parseAndFormatDate, checkForAssignedChartColor } from 'app/utils';
import 'chartjs-plugin-annotation';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Line } from 'react-chartjs-2';
import NoData from './NoData';

const PolicyViolationChart = ({ MileStones, duration }) => {
  const {
    trends: { dataClassLevels = [] },
    trendsLoading,
  } = useSelector(state => state.compliance.trendsData);
  const { t } = useTranslation('translation', { keyPrefix: 'compliance' });

  const chartColors = useSelector(state => state?.userSettings?.settings?.chartColors) || {};

  const getColor = id => {
    return chartColors[id] || checkForAssignedChartColor(id);
  };

  const formattedData = dataClassLevels?.map(classLevel => ({
    ...classLevel,
    label: classLevel?.name,
    data: classLevel?.dataPoints?.map(dataPoint => ({
      x: parseAndFormatDate(dataPoint.date),
      y: dataPoint?.nrOfDeviations,
      data: classLevel?.label + ': ' + dataPoint?.nrOfDeviations,
      color: 'red',
    })),
    color: getColor(classLevel?.id),
  }));

  const chartData = {
    datasets: formattedData?.map(classLevelData => ({
      label: classLevelData?.label,
      data: classLevelData?.data,
      borderColor: getColor(classLevelData?.id),
      pointBackgroundColor: getColor(classLevelData?.id),
      tension: 0.4,
      borderWidth: duration <= 12 ? 3 : 2,
      pointRadius: duration <= 12 ? 1 : 1,
      pointHitRadius: 20,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        time: {
          parser: 'DD MMM YYYY',
          unit: 'month',
        },
        ticks: {
          color: ColorsV2.white,
        },
        title: {
          display: true,
          text: t('timeline'),
          color: ColorsV2.white,
        },
        min: calculateMinDate(duration).toDate(),
        max: Date.now(),
      },
      y: {
        title: {
          display: true,
          text: t('number_of_deviations'),
          color: ColorsV2.white,
        },
        ticks: {
          color: ColorsV2.white,
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      datalabels: {
        display: false,
        color: 'black',
        align: 'end',
        padding: {
          right: 2,
        },
        labels: {
          padding: { top: 100 },
          value: {
            color: ColorsV2.white,
          },
        },
        formatter(value) {
          return '\n' + value?.y?.toFixed(0);
        },
      },
    },
    animation: false,
    height: 500,
  };

  if (trendsLoading) {
    return (
      <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <CircularProgress size='4rem' sx={{ color: ColorsV2.acent }} />
      </Box>
    );
  }

  return (
    <>
      {dataClassLevels?.length ? (
        <Line data={chartData} plugins={[ChartDataLabels, MileStones]} options={options} />
      ) : (
        <NoData />
      )}
    </>
  );
};

export default PolicyViolationChart;
